/**
 * OTP Auth Service
 *
 * Thin wrapper around Supabase Phone Auth (Twilio Verify).
 * Handles OTP send/verify, profile lookup by phone, auth-to-profile linking,
 * and the shared hydration logic used by both explicit OTP and silent session recovery.
 *
 * First module in the codebase to use supabase.auth.
 */

import { supabase, signOutIntentionally } from '../supabase';
import { isValidProfileUserId, clearAnonIdentity } from '../../utils/identityUtils';
import { checkAndRecoverData, topUpMissingConnections } from '../storage/dataRecovery';
import { logEvent, getProfileUsername } from '../analytics/eventLogger';
import { registerDevice, broadcastInvalidation } from '../session/deviceSession';
import { convertPendingProfile } from './pendingConversion';
import { setStoredTier, tierFromAccountStatus, TIER_FULL } from '../../utils/userTier';

/**
 * The one answer for a number that may not log in — not a Full account and
 * not a live, invited pending entry (Mark's copy; registered in ui-design-system.md).
 */
export const INVITE_REQUIRED_MESSAGE = 'Thank you but you need an invite!';

/**
 * Send OTP to a phone number
 * @param {string} phone - E.164 format phone number
 * @returns {{ error: string|null }}
 */
export const sendOtp = async (phone) => {
  // Phone guard, decided by the server (041 may_request_code): a code goes
  // only to a Full account, or to a live pending entry that was invited (or
  // when the invite_required switch is off). A Simple profile converts through
  // pending, never through login (auth.md § Account Tiers). Anything else gets
  // the invite message, no SMS. The server refuses an un-invited conversion
  // either way; this check spares them a code that can't be used.
  const normalizedPhone = phone.startsWith('+') ? phone : `+${phone}`;

  const { data: eligible, error: guardError } = await supabase.rpc('may_request_code', {
    p_phone: normalizedPhone,
  });

  if (guardError) {
    console.error('[OTP_AUTH] sendOtp phone guard failed:', guardError.message);
    return { error: guardError.message };
  }

  if (!eligible) {
    console.warn('[OTP_AUTH] Phone not eligible:', normalizedPhone.slice(-4));
    return { error: INVITE_REQUIRED_MESSAGE };
  }

  const { error } = await supabase.auth.signInWithOtp({ phone });
  if (error) {
    console.error('[OTP_AUTH] sendOtp failed:', error.message);
    return { error: error.message };
  }
  return { error: null };
};

/**
 * Verify OTP code
 * @param {string} phone - E.164 format phone number
 * @param {string} token - 6-digit OTP code
 * @returns {{ session: object|null, error: string|null }}
 */
export const verifyOtp = async (phone, token) => {
  const { data, error } = await supabase.auth.verifyOtp({
    phone,
    token,
    type: 'sms',
  });
  if (error) {
    console.error('[OTP_AUTH] verifyOtp failed:', error.message);
    return { session: null, error: error.message };
  }
  reportLoginApproval(data.session);
  return { session: data.session, error: null };
};

/**
 * Tell Twilio this login completed, so Fraud Guard works with custom codes
 * (auth.md § OTP Phone Guard). Fire-and-forget: never fails the login.
 * @param {object|null} session - the session Supabase just returned
 */
export const reportLoginApproval = (session) => {
  if (!session?.access_token) return;
  supabase.functions
    .invoke('sms-approve', { headers: { Authorization: `Bearer ${session.access_token}` } })
    .then(({ error }) => {
      if (error) console.warn('[OTP_AUTH] sms-approve report failed:', error.message);
    })
    .catch((err) => console.warn('[OTP_AUTH] sms-approve report failed:', err?.message));
};

/**
 * Get current auth session (if any)
 * @returns {object|null} session or null
 */
export const getAuthSession = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.error('[OTP_AUTH] getAuthSession failed:', error.message);
    return null;
  }
  return data.session;
};

/**
 * Sign out — clear Supabase auth session (intentional, not refresh failure)
 */
export const signOut = async () => {
  const { error } = await signOutIntentionally();
  if (error) {
    console.error('[OTP_AUTH] signOut failed:', error.message);
  }
};

/**
 * Look up the FULL account that owns this session's verified phone number. A
 * Simple profile holding the number is not a login target — it falls through
 * to pending conversion.
 * @returns {string|null} profile_user_id or null
 */
export const lookupMyFullProfileId = async () => {
  // The server reads the number from the verified session itself
  // (my_full_profile_id — open-access lockdown): another person's profile row
  // isn't readable, and a typed number is never trusted.
  const { data, error } = await supabase.rpc('my_full_profile_id');

  if (error) {
    console.error('[OTP_AUTH] lookupMyFullProfileId failed:', error.message);
    return null;
  }
  return data || null;
};

/**
 * Link auth user to profile — calls relink_auth_user_id RPC to update auth_user_id
 * on ALL tables (profiles, connection_data, connections, chat_messages, active_sessions).
 * Validates ownership via phone from JWT; the server refuses a non-Full profile
 * (migration 038). Never throws — returns the error for the caller to act on.
 * hydrateFromAuth awaits it (f/identity-ownership): the reload that follows
 * used to cut a fire-and-forget relink off mid-flight.
 * @param {string} profileUserId - Our profile_user_id
 * @returns {{ error: string|null }}
 */
export const linkAuthToProfile = async (profileUserId) => {
  try {
    const { error } = await supabase.rpc('relink_auth_user_id', {
      p_profile_user_id: profileUserId,
    });
    if (error) {
      console.error('[OTP_AUTH] relink_auth_user_id failed:', error.message);
      return { error: error.message };
    }
    return { error: null };
  } catch (error) {
    console.error('[OTP_AUTH] linkAuthToProfile failed:', error);
    return { error: error.message || String(error) };
  }
};

/** The server's tier refusal (relink_auth_user_id v3, migration 038). */
const TIER_REFUSAL = 'profile is not a full account';

/**
 * A refused login: drop the phone session so nothing half-signed-in remains,
 * and answer with the invite message.
 */
const refuseLogin = async (reason) => {
  console.warn('[OTP_AUTH] Login refused:', reason);
  await signOut();
  return { error: INVITE_REQUIRED_MESSAGE };
};

/**
 * Shared hydration logic — used by both explicit OTP (Step 4) and silent session recovery (Step 5)
 *
 * Flow:
 * 1. Look up the Full account by phone, else convert a live pending entry
 *    (in place for a Simple profile), else refuse with the invite message
 * 2. Validate the ID format
 * 3. Link auth user to profile (awaited; a refusal signs out and stops here)
 * 4. Write profile_user_id to localStorage, register this device
 * 5. Recover all data from Supabase
 * 6. Set transition flags and reload
 *
 * @param {object} authSession - Supabase auth session
 * @returns {{ error: string|null }} - error message if hydration failed
 */
export const hydrateFromAuth = async (authSession) => {
  const phone = authSession.user.phone;
  if (!phone) {
    return { error: 'No phone number in auth session' };
  }

  // Path 1: Look up the Full account on this number
  let profileUserId = await lookupMyFullProfileId();
  let convertedInPlace = false;
  // The tier the server answered with — Path 1's relink takes Full only
  let serverTier = TIER_FULL;

  if (profileUserId) {
    // Existing profile — standard hydration
    console.log('[OTP_AUTH] Path 1: Existing profile found:', profileUserId);
  } else {
    // Path 2: Try pending profile conversion
    console.log('[OTP_AUTH] Path 2: No profile found, trying pending conversion...');
    const conversionResult = await convertPendingProfile();

    if (conversionResult.error) {
      // Path 3: neither a Full account nor a live pending entry
      return refuseLogin(`Path 3: ${conversionResult.error}`);
    }

    profileUserId = conversionResult.profileUserId;
    convertedInPlace = conversionResult.convertedInPlace;
    // null from a pre-038 server → no copy written; the next boot seeds it
    serverTier = tierFromAccountStatus(conversionResult.accountStatus);

    // Set sessionChatRoomId so PWA opens WebSocket channel after reload
    if (conversionResult.chatRoomId) {
      sessionStorage.setItem('sessionChatRoomId', conversionResult.chatRoomId);
    }

    // Store onboarding data — App.js checks these after reload. A Simple
    // profile converted in place already has its name and data: no onboarding.
    if (!convertedInPlace) {
      localStorage.setItem('needs_onboarding', 'true');
      if (conversionResult.anonName) {
        localStorage.setItem('onboarding_prefill_name', conversionResult.anonName);
      }
      // Pre-fill phone from the verified OTP number
      const normalizedPhone = phone.startsWith('+') ? phone : `+${phone}`;
      localStorage.setItem('onboarding_prefill_phone', normalizedPhone);
    }

    console.log(
      `[OTP_AUTH] Conversion complete: ${profileUserId}, ` +
        `${conversionResult.connectionsMigrated} connections, ` +
        `${conversionResult.messagesMigrated} messages migrated`
    );
  }

  // Validate format
  if (!isValidProfileUserId(profileUserId)) {
    console.error('[OTP_AUTH] Invalid profile_user_id format:', profileUserId);
    return { error: 'Invalid account data' };
  }

  // Link auth → profile — awaited, FIRST: the server's tier refusal (Full
  // accounts only) must end the login before this device writes an identity
  // or kicks the owner's other device. Also lands before the reload, which
  // used to cut a fire-and-forget relink off. An in-place conversion was
  // relinked inside the conversion itself — skipped. Any other relink error
  // is logged and the login continues, as before 038 (a network blip must not
  // read as "you need an invite").
  if (!convertedInPlace) {
    const link = await linkAuthToProfile(profileUserId);
    if (link.error?.includes(TIER_REFUSAL)) {
      return refuseLogin(`relink: ${link.error}`);
    }
  }

  // Write identity to localStorage
  localStorage.setItem('profile_user_id', profileUserId);
  localStorage.setItem('user_type', 'profile');
  localStorage.setItem('phone_verified', 'true');
  // The phone's copy of the tier, from the server's answer (auth.md § Account Tiers)
  if (serverTier) setStoredTier(serverTier);

  // Device session enforcement — register this device, invalidate old ones
  try {
    await registerDevice(profileUserId);
    await broadcastInvalidation(profileUserId);
    logEvent('session_invalidated', {
      reason: 'new_device_login',
      profileUsername: getProfileUsername(),
    });
  } catch (error) {
    // Non-blocking — if this fails, old device catches it via visibility check
    console.error('[OTP_AUTH] Device session enforcement failed:', error);
  }

  // Recover data from Supabase
  try {
    await checkAndRecoverData();
    // The server merged every Simple profile on this number into the account
    // (039): bring their rooms onto the phone — no count guess
    await topUpMissingConnections(profileUserId);
  } catch (error) {
    console.error('[OTP_AUTH] Data recovery failed:', error);
    logEvent('pwa_hydration_failed', {
      error: error.message,
      recovery_type: 'otp',
      profileUsername: getProfileUsername(),
    });
    // Continue anyway — partial data is better than no data
  }

  // Clear OTP-required flag — the relink RPC has fired, mismatch is resolved.
  // Must clear before reload, otherwise DemoGate step 7.4 catches it again.
  sessionStorage.removeItem('pending_otp_required');

  // Drop the leftover anon identity now that the profile login is established — done late, just
  // before reload, so nothing recreates it before the reloaded page reads it. Keeps the handoff
  // relay asleep on Android's shared storage (see clearAnonIdentity). Render-safe: the convert
  // RPC re-keys sender_id server-side and messageStore normalizes on read.
  clearAnonIdentity();

  // Set transition flags and reload
  sessionStorage.setItem('user_type', 'profile');
  sessionStorage.setItem('hydration_complete', 'true');
  logEvent('pwa_hydration_success', {
    recovery_type: 'otp',
    profileUsername: getProfileUsername(),
  });
  window.location.reload();

  // Won't reach here due to reload, but satisfy the return type
  return { error: null };
};
